import {
    CallExpressionShape,
    Plugin,
    unloadBuilder,
    type ObjectShape,
    type OutputFile,
    type Record,
} from '@servicenow/sdk-build-core'
import { NowIdShape, NowIncludeShape } from '@servicenow/sdk-build-plugins'
import {
    computeAgentConfigValue,
    computeDataAccessValue,
    computeSecurityAclValue,
    computeVersionDetailsValue,
    mapToolsFromM2M,
    mapTriggerConfigs,
} from './ai-agent-transform-helper'
import {
    createAgentConfigRecord,
    createAgentRecord,
    createAgentToolM2MRecord,
    createAgentToolRecord,
    createAgentVersionRecords,
    createCategoryMappingRecord,
    createDataAccessConfigRecord,
    createTriggerConfigRecord,
    createTriggerMappingRecord,
    extractPublishedInstructions,
} from './ai-agent-shapes-helper'
import {
    CHANNEL_SYS_ID_TO_NAME_MAP,
    DEFAULT_CONTEXT_PROCESSING_SCRIPT,
    GEN_AI_AGENT_TYPE,
    MEMORY_CATEGORY_TO_SYS_ID_MAP,
    OOB_TOOL_TYPE_TO_SYS_ID_MAP,
    SN_AIA_LTM_CATEGORY_MAPPING,
    SN_AIA_TRIGGER_CONFIG,
    SN_AIA_TRIGGER_MAPPING,
    SN_AI_AGENT,
    SN_AI_AGENT_CONFIG,
    SN_AI_AGENT_TOOL,
    SN_AI_AGENT_TOOL_M2M,
    SN_AI_AGENT_VERSION,
    SYS_AGENT_ACCESS_ROLE_CONFIG,
    SYS_AGENT_ACCESS_ROLE_MAPPING,
    SYS_ID_TO_MEMORY_CATEGORY_MAP,
    SYS_SECURITY_ACL,
    SYS_SECURITY_ACL_ROLE,
    SYS_USER_ROLE,
} from './constants'
import { processSecurityAcl } from './security-acl-processor'

/**
 * Normalize a script string for comparison by removing whitespace variations
 * This allows us to compare scripts even if they have different indentation or line endings
 */
function normalizeScript(script: string | undefined): string {
    if (!script) {
        return ''
    }
    // Remove all whitespace and normalize line endings for comparison
    return script.replace(/\s+/g, ' ').trim()
}

/**
 * Precomputed normalized default script to avoid redundant computation
 */
const NORMALIZED_DEFAULT_CONTEXT_PROCESSING = normalizeScript(DEFAULT_CONTEXT_PROCESSING_SCRIPT)

export const AiAgentPlugin = Plugin.create({
    name: 'AiAgentPlugin',
    docs: [
        {
            apiName: 'AiAgent',
            tags: ['sn_aia_agent'],
        },
    ],
    records: {
        [SN_AI_AGENT]: {
            relationships: {
                [SN_AI_AGENT_CONFIG]: {
                    via: 'agent',
                    descendant: true,
                },
                [SN_AI_AGENT_VERSION]: {
                    via: 'target_id',
                    descendant: true,
                },
                [SN_AI_AGENT_TOOL_M2M]: {
                    via: 'agent',
                    descendant: true,
                    relationships: {
                        [SN_AI_AGENT_TOOL]: {
                            via: 'tool',
                            inverse: true,
                            descendant: true,
                        },
                    },
                },
                [SYS_AGENT_ACCESS_ROLE_CONFIG]: {
                    via: 'agent',
                    descendant: true,
                    relationships: {
                        [SYS_AGENT_ACCESS_ROLE_MAPPING]: {
                            via: 'agent_access_config',
                            descendant: true,
                            relationships: {
                                [SYS_USER_ROLE]: {
                                    via: 'role',
                                    inverse: true,
                                },
                            },
                        },
                    },
                },
                [SN_AIA_TRIGGER_MAPPING]: {
                    via: 'related_resource_record',
                    descendant: true,
                    relationships: {
                        [SN_AIA_TRIGGER_CONFIG]: {
                            via: 'trigger_configuration',
                            inverse: true,
                            descendant: true,
                        },
                    },
                },
                [SN_AIA_LTM_CATEGORY_MAPPING]: {
                    via: 'entity_id',
                    descendant: true,
                },
                [SYS_SECURITY_ACL]: {
                    via: [{ parent: ['internal_name', '\\.(.*)'], child: ['name', '\\.(.*)'] }],
                    descendant: true,
                    relationships: {
                        [SYS_SECURITY_ACL_ROLE]: {
                            via: 'sys_security_acl',
                            descendant: true,
                        },
                    },
                },
            },
            async toShape(record, { transform, descendants, database }) {
                // Process context processing script with NowIncludeShape
                // BUT only if it doesn't match the default value

                let contextProcessingScript: NowIncludeShape | undefined

                if (record.has('context_processing_script')) {
                    const scriptValue = record.get('context_processing_script').ifString()?.getValue()
                    const normalizedScript = normalizeScript(scriptValue)

                    // Only create NowIncludeShape if script doesn't match default
                    if (scriptValue && normalizedScript !== NORMALIZED_DEFAULT_CONTEXT_PROCESSING) {
                        contextProcessingScript = await NowIncludeShape.fromRecord(
                            record,
                            record.get('context_processing_script'),
                            transform
                        )
                    }
                }

                // Get agent's data
                const config = descendants.query(SN_AI_AGENT_CONFIG)[0]
                const versions = descendants.query(SN_AI_AGENT_VERSION)
                const toolM2Ms = descendants.query(SN_AI_AGENT_TOOL_M2M)
                const toolRecords = descendants.query(SN_AI_AGENT_TOOL)
                const dataAccessConfig = descendants.query(SYS_AGENT_ACCESS_ROLE_CONFIG)[0]
                const roleMappings = descendants.query(SYS_AGENT_ACCESS_ROLE_MAPPING)
                const triggerMappings = descendants.query(SN_AIA_TRIGGER_MAPPING)
                const triggerConfigs = descendants.query(SN_AIA_TRIGGER_CONFIG)
                const categoryMappings = descendants.query(SN_AIA_LTM_CATEGORY_MAPPING)

                // Look up the ACL record from the database by internal_name + agent ACL type,
                // consistent with how AiAgenticWorkflow resolves its ACL in toShape.
                // Fall back to the raw sys_id stored on the record if no match is found.
                const internalName = record.get('internal_name').ifString()?.getValue() || ''
                const matchingAcl = database.get(SYS_SECURITY_ACL, {
                    name: internalName,
                    type: GEN_AI_AGENT_TYPE,
                })
                // If the ACL was auto-generated via securityAcl, reconstruct that config.
                // sys_security_acl_role records are added as siblings via .with() in toRecord,
                // so they appear in descendants. Filter to only those linked to this agent's ACL.
                const matchingAclId = matchingAcl?.getId().getValue()
                const aclRoleRecords = matchingAclId
                    ? descendants.query(SYS_SECURITY_ACL_ROLE).filter((r) => {
                          const aclRef = r.get('sys_security_acl')
                          const refId = aclRef?.isRecord() ? aclRef.getId().getValue() : aclRef?.ifString()?.getValue()
                          return refId === matchingAclId
                      })
                    : []
                // Emit securityAcl when role records are present (auto-generated ACL),
                // otherwise fall back to the raw acl field reference.
                const securityAclValue = computeSecurityAclValue(matchingAcl, aclRoleRecords)

                // Build tools array with M2M and tool record
                const tools = toolM2Ms.length > 0 ? await mapToolsFromM2M(toolM2Ms, toolRecords, transform) : []

                // Compute flattened agentConfig values before transform
                const agentConfigValues = computeAgentConfigValue(config)

                // Compute versionDetails before transform
                const versionDetailsValue = computeVersionDetailsValue(versions)

                // Compute dataAccess before transform
                const dataAccessValue = computeDataAccessValue(dataAccessConfig, roleMappings)

                // Normalize empty strings to defaults for union-typed fields
                const agentTypeValue = record.get('agent_type')?.ifString()?.getValue() || 'internal'
                const channelValue = record.get('channel')?.ifString()?.getValue() || 'nap_and_va'

                // Only include tools property if there are actual tools
                return {
                    success: true,
                    value: new CallExpressionShape({
                        source: record,
                        callee: 'AiAgent',
                        args: [
                            record.transform(({ $ }) => ({
                                $id: $.val(NowIdShape.from(record) ?? record.getId().getValue()),
                                name: $.from('name'),
                                description: $.from('description'),
                                externalAgentConfiguration: $.from('external_agent_configuration').def(''),
                                external: $.from('external').toBoolean().def(false),
                                active: $.from('active').toBoolean().def(true),
                                parent: $.from('parent').def(''),
                                agentRole: $.from('role'),
                                recordType: $.from('record_type').def('template'),
                                contextProcessingScript: contextProcessingScript
                                    ? $.val(contextProcessingScript)
                                    : $.val(undefined),
                                agentType: $.val(agentTypeValue).def('internal'),
                                sysDomain: $.from('sys_domain').def('global'),
                                sourceId: $.from('source_id').def(''),
                                processingMessage: $.from('processing_message').def(''),
                                postProcessingMessage: $.from('post_processing_message').def(''),
                                advancedMode: $.from('advanced_mode').toBoolean().def(false),
                                channel: $.val(channelValue).def('nap_and_va'),
                                condition: $.from('condition').def(''),
                                compiledHandbook: $.from('compiled_handbook').def(''),
                                versionDetails: $.val(versionDetailsValue),
                                public: $.val(agentConfigValues?.public),
                                agentLearning: $.val(agentConfigValues?.agentLearning),
                                docUrl: $.val(agentConfigValues?.docUrl),
                                iconUrl: $.val(agentConfigValues?.iconUrl),
                                runAsUser: $.val(agentConfigValues?.runAsUser),
                                agentConfigSysOverrides: $.val(agentConfigValues?.agentConfigSysOverrides),
                                agentDescriptor: $.val(agentConfigValues?.agentDescriptor),
                                tools: $.val(tools.length > 0 ? tools : undefined),
                                securityAcl: $.val(securityAclValue),
                                dataAccess: $.val(dataAccessValue),
                                triggerConfig: $.val(
                                    triggerConfigs.length > 0 ? mapTriggerConfigs(triggerConfigs, triggerMappings) : []
                                ),
                                memoryCategories: $.val(
                                    categoryMappings.length > 0
                                        ? categoryMappings
                                              .map((m) => {
                                                  const categorySysId = m.get('category')?.ifString()?.getValue()
                                                  if (!categorySysId) {
                                                      return undefined
                                                  }
                                                  // Use known name from map, fall back to raw sys_id
                                                  return SYS_ID_TO_MEMORY_CATEGORY_MAP[categorySysId] ?? categorySysId
                                              })
                                              .filter(Boolean)
                                        : undefined
                                ),
                            })),
                        ],
                    }),
                }
            },
            async toFile(record, context) {
                const { config, transform, descendants, database } = context

                // Check for DELETE-action sys_security_acl_role records scoped to this agent's ACLs
                // (descendants only has INSERT_OR_UPDATE, so DELETE records are missed)
                const agentAclIds = new Set(descendants.query(SYS_SECURITY_ACL).map((r) => r.getId().getValue()))
                const deleteAclRoles = database.query(SYS_SECURITY_ACL_ROLE).filter((rec) => {
                    if (rec.getAction() !== 'DELETE') {
                        return false
                    }
                    const aclRef = rec.get('sys_security_acl')?.toString().getValue()
                    return aclRef ? agentAclIds.has(aclRef) : false
                })

                // No DELETE records — use default serialization
                if (deleteAclRoles.length === 0) {
                    return { success: false }
                }

                // Custom serialization to include DELETE XMLs
                const recordBuilder = unloadBuilder(config)
                const updateName = await transform.getUpdateName(record)
                const builder = recordBuilder.record(record, updateName)

                record
                    .entries()
                    .sort(([a], [b]) => a.localeCompare(b))
                    .forEach(([prop, shape]) => {
                        builder.field(prop, shape)
                    })

                // Helper to serialize a descendant record
                const serializeDescendant = async (descendantRecord: Record) => {
                    const descUpdateName = await transform.getUpdateName(descendantRecord)
                    const descBuilder = unloadBuilder(config)
                    const descRecordBuilder = descBuilder.record(descendantRecord, descUpdateName)

                    descendantRecord
                        .entries()
                        .sort(([a], [b]) => a.localeCompare(b))
                        .forEach(([prop, shape]) => {
                            descRecordBuilder.field(prop, shape)
                        })

                    return {
                        source: descendantRecord,
                        name: `${descUpdateName}.xml`,
                        category: descendantRecord.getInstallCategory(),
                        content: descBuilder.end(),
                    }
                }

                const descendantFiles: OutputFile[] = []

                // Serialize all standard descendant tables
                for (const table of [
                    SN_AI_AGENT_CONFIG,
                    SN_AI_AGENT_VERSION,
                    SN_AI_AGENT_TOOL_M2M,
                    SN_AI_AGENT_TOOL,
                    SYS_AGENT_ACCESS_ROLE_CONFIG,
                    SYS_AGENT_ACCESS_ROLE_MAPPING,
                    SN_AIA_TRIGGER_MAPPING,
                    SN_AIA_TRIGGER_CONFIG,
                    SN_AIA_LTM_CATEGORY_MAPPING,
                    SYS_SECURITY_ACL,
                    SYS_SECURITY_ACL_ROLE,
                ]) {
                    for (const rec of descendants.query(table)) {
                        descendantFiles.push(await serializeDescendant(rec))
                    }
                }

                // Emit DELETE XMLs for deleted sys_security_acl_role records
                for (const rec of deleteAclRoles) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                return {
                    success: true,
                    value: [
                        {
                            source: record,
                            name: `${updateName}.xml`,
                            category: record.getInstallCategory(),
                            content: recordBuilder.end(),
                        },
                        ...descendantFiles,
                    ],
                }
            },
        },
        [SN_AI_AGENT_CONFIG]: {
            coalesce: ['agent'],
        },
        [SN_AI_AGENT_VERSION]: {
            coalesce: ['target_id', 'version_name'],
        },
        [SN_AI_AGENT_TOOL]: {
            composite: true,
            coalesce: ['name'],
            async toShape(record, { transform }) {
                // Process script fields with NowIncludeShape exactly like in BusinessRule plugin
                let script: NowIncludeShape | undefined
                let postProcessingScript: NowIncludeShape | undefined

                // Only process script fields if they exist in the record
                if (record.has('script')) {
                    script = await NowIncludeShape.fromRecord(record, record.get('script'), transform)
                }

                if (record.has('post_processing_script')) {
                    postProcessingScript = await NowIncludeShape.fromRecord(
                        record,
                        record.get('post_processing_script'),
                        transform
                    )
                }

                return {
                    success: true,
                    value: record.transform(({ $ }) => ({
                        name: $,
                        description: $,
                        targetDocument: $.from('target_document'),
                        active: $.toBoolean().map((v) => {
                            const value = v.getValue()
                            return value === true ? undefined : value
                        }),
                        recordType: $.from('record_type').def('template'),
                        type: $,
                        script: script ? $.val(script) : $.from('script'),
                        postProcessingScript: postProcessingScript
                            ? $.val(postProcessingScript)
                            : $.from('post_processing_script'),
                        sysOverrides: $.from('sys_overrides'),
                    })),
                }
            },
        },
        [SN_AI_AGENT_TOOL_M2M]: {
            coalesce: ['agent', 'tool', 'name'],
        },
        [SYS_AGENT_ACCESS_ROLE_CONFIG]: {
            coalesce: ['agent'],
        },
        [SYS_AGENT_ACCESS_ROLE_MAPPING]: {
            coalesce: ['agent_access_config', 'role'],
        },
        [SYS_SECURITY_ACL_ROLE]: {
            coalesce: ['sys_security_acl', 'sys_user_role'],
        },
        [SN_AIA_TRIGGER_CONFIG]: {
            coalesce: ['name'],
            async toShape(record, { transform }) {
                // Trigger configs that belong to a usecase are handled by the
                // ai-agentic-workflow plugin as descendants — skip them here.
                const usecaseRef = record.get('usecase')?.ifString()?.ifNotEmpty()?.getValue()
                if (usecaseRef) {
                    return { success: false }
                }

                // Process script fields with NowIncludeShape
                let runAsScript: NowIncludeShape | undefined

                if (record.has('run_as_script')) {
                    runAsScript = await NowIncludeShape.fromRecord(record, record.get('run_as_script'), transform)
                }

                // Build schedule object from flat DB fields
                const runDayOfMonth = record.get('run_dayofmonth')?.ifString()?.ifNotEmpty()?.toNumber()?.getValue()
                const runDayOfWeek = record.get('run_dayofweek')?.ifString()?.ifNotEmpty()?.toNumber()?.getValue()
                const repeatInterval = record.get('run_period')?.ifString()?.getValue()
                const starting = record.get('run_start')?.ifString()?.getValue()
                const time = record.get('run_time')?.ifString()?.getValue()
                const triggerStrategy = record.get('trigger_strategy')?.ifString()?.getValue()

                const schedule =
                    runDayOfMonth || runDayOfWeek || repeatInterval || starting || time || triggerStrategy
                        ? {
                              ...(runDayOfMonth && { runDayOfMonth }),
                              ...(runDayOfWeek && { runDayOfWeek }),
                              ...(repeatInterval && { repeatInterval }),
                              ...(starting && { starting }),
                              ...(time && { time }),
                              ...(triggerStrategy && { triggerStrategy }),
                          }
                        : undefined

                // Reverse-map channel sys_id to OOB name
                const channelSysId = record.get('channel')?.ifString()?.getValue()
                const channelValue = channelSysId ? CHANNEL_SYS_ID_TO_NAME_MAP[channelSysId] || channelSysId : undefined

                // Exclude portal when it holds the literal string 'undefined' or is empty
                const portalValue = record.get('portal')?.ifString()?.getValue()
                const includePortal = Boolean(portalValue && portalValue !== 'undefined')

                return {
                    success: true,
                    value: record.transform(({ $ }) => ({
                        name: $,
                        description: $,
                        active: $.toBoolean().map((v) => {
                            const value = v.getValue()
                            return value === true ? undefined : value
                        }),
                        targetTable: $.from('target_table'),
                        triggerCondition: $.from('condition').def(''),
                        triggerFlowDefinitionType: $.from('trigger_flow_definition_type'),
                        schedule: $.val(schedule),
                        runAs: $.from('run_as'),
                        runAsUser: $.from('run_as_user'),
                        notificationScript: $.from('notification_script'),
                        objectiveTemplate: $.from('objective_template'),
                        runAsScript: runAsScript ? $.val(runAsScript) : $.from('run_as_script'),
                        channel: $.val(channelValue),
                        showNotifications: $.from('show_notifications')
                            .toBoolean()
                            .map((v) => {
                                const value = v.getValue()
                                return value === false ? undefined : value
                            }),
                        domain: $.from('sys_domain').def('global'),
                        enableDiscovery: $.from('enable_discovery')
                            .toBoolean()
                            .map((v) => {
                                const value = v.getValue()
                                return value === false ? undefined : value
                            }),
                        businessRule: $.from('business_rule'),
                        profile: $.from('profile'),
                        ...(includePortal ? { portal: $.from('portal') } : {}),
                        sysOverrides: $.from('sys_overrides'),
                    })),
                }
            },
        },
        [SN_AIA_TRIGGER_MAPPING]: {
            coalesce: ['trigger_configuration', 'related_resource_record'],
        },
        [SN_AIA_LTM_CATEGORY_MAPPING]: {
            coalesce: ['entity_id', 'category'],
        },
    },
    shapes: [
        {
            shape: CallExpressionShape,
            fileTypes: ['fluent'],
            async toRecord(callExpression, { factory, diagnostics, config }) {
                if (callExpression.getCallee() !== 'AiAgent') {
                    return { success: false }
                }

                const arg = callExpression.getArgument(0).asObject()
                const tools = arg.get('tools').ifArray()
                const dataAccess = arg.get('dataAccess')?.ifObject()
                // runAsUser is now a top-level field
                const runAsUser = arg.get('runAsUser')

                const securityAclField = arg.get('securityAcl')?.ifObject()
                if (!securityAclField) {
                    diagnostics.error(arg, 'AI Agent must have a securityAcl field.')
                    return { success: false }
                }

                // Get values for computing internal_name
                const agentName = arg.get('name').ifString()?.getValue() || ''
                const sysDomain = arg.get('sysDomain')?.ifString()?.getValue() || 'global'
                const scopeName = config.scope

                // Compute internal_name as: domain + scope + agent name
                const computedInternalName = `${sysDomain}.${scopeName}.${agentName}`

                // Extract published instructions and validate only one version is published
                const { instructions: publishedInstructions, publishedCount } = extractPublishedInstructions(arg)
                if (publishedCount > 1) {
                    const versionDetailsField = arg.get('versionDetails')
                    diagnostics.error(
                        versionDetailsField || arg,
                        `AI Agent '${agentName}' has multiple versions with state 'published'. Only one version can be published at a time.`
                    )
                    return { success: false }
                }

                // If securityAcl is provided, auto-generate the ACL + role records.
                // The ACL name matches the agent's internal_name so the existing toShape
                // database.get() lookup (by name + GEN_AI_AGENT_TYPE) will find it.
                let securityAclRecords: Awaited<ReturnType<typeof processSecurityAcl>> | undefined
                if (securityAclField) {
                    securityAclRecords = await processSecurityAcl(
                        securityAclField,
                        computedInternalName,
                        factory,
                        diagnostics
                    )
                }

                const agentRecord = await createAgentRecord(
                    factory,
                    callExpression,
                    computedInternalName,
                    SN_AI_AGENT,
                    publishedInstructions,
                    securityAclRecords?.aclRecord.getId().getValue()
                )

                // Generating Agent config record (reads flattened fields from arg)
                const agentConfigRecord = await createAgentConfigRecord(
                    factory,
                    callExpression,
                    agentRecord.getId(),
                    arg,
                    SN_AI_AGENT_CONFIG
                )

                // Generating Agent version records
                const agentVersionRecords = await createAgentVersionRecords(
                    factory,
                    callExpression,
                    agentRecord.getId(),
                    SN_AI_AGENT_VERSION,
                    SN_AI_AGENT
                )

                let dataAccessRecords: Awaited<ReturnType<typeof createDataAccessConfigRecord>> | undefined
                if (dataAccess) {
                    const runAsUserValue = runAsUser?.ifString()?.getValue()

                    dataAccessRecords = await createDataAccessConfigRecord(
                        factory,
                        callExpression,
                        agentRecord.getId(),
                        dataAccess,
                        SYS_AGENT_ACCESS_ROLE_CONFIG,
                        SN_AI_AGENT,
                        diagnostics,
                        !!runAsUserValue
                    )
                }

                // Handle triggerConfig array
                const triggerConfigs = arg.get('triggerConfig')?.ifArray()?.getElements() ?? []
                const triggerConfigRecords: Array<{ record: Record; config: ObjectShape }> = []

                for (const triggerConfig of triggerConfigs) {
                    if (triggerConfig?.isObject()) {
                        // Compute internal_name for trigger config: domain + scope + trigger name
                        const triggerName = triggerConfig.get('name')?.ifString()?.getValue() || ''
                        const triggerDomain = triggerConfig.get('domain')?.ifString()?.getValue() || 'global'
                        const computedTriggerInternalName = `${triggerDomain}.${scopeName}.${triggerName}`

                        const triggerConfigRecord = await createTriggerConfigRecord(
                            factory,
                            callExpression,
                            triggerConfig,
                            computedTriggerInternalName,
                            SN_AIA_TRIGGER_CONFIG
                        )
                        triggerConfigRecords.push({ record: triggerConfigRecord, config: triggerConfig })
                    }
                }

                // Create triggerMapping records - one for each triggerConfig
                const triggerMappingRecords: Record[] = []
                for (const { record: triggerConfigRecord, config: triggerConfig } of triggerConfigRecords) {
                    const triggerMappingRecord = await createTriggerMappingRecord(
                        factory,
                        callExpression,
                        agentRecord.getId(),
                        triggerConfig,
                        triggerConfigRecord.getId(),
                        SN_AIA_TRIGGER_MAPPING
                    )
                    triggerMappingRecords.push(triggerMappingRecord)
                }

                // Validate: ensure no two tools have the same name
                if (tools) {
                    const toolNames = new Set<string>()
                    const toolElements = tools.getElements()

                    for (const tool of toolElements) {
                        const toolObj = tool.asObject()
                        const toolName = toolObj.get('name')?.ifString()?.getValue()

                        if (toolName) {
                            if (toolNames.has(toolName)) {
                                diagnostics.error(
                                    toolObj.get('name'),
                                    `Duplicate tool name "${toolName}". Each tool must have a unique name.`
                                )
                                return { success: false }
                            }
                            toolNames.add(toolName)
                        }

                        // Validate RAG tools
                        const toolType = toolObj.get('type')?.ifString()?.getValue()
                        if (toolType === 'rag' || toolType === 'search_retrieval') {
                            const inputs = toolObj.get('inputs')?.ifObject()

                            // Validate that inputs field is provided for RAG tools
                            if (!inputs) {
                                diagnostics.error(
                                    toolObj,
                                    `RAG tool '${toolName}' must have an 'inputs' field. The inputs configuration is mandatory for RAG tools.`
                                )
                                return { success: false }
                            }

                            // Validate documentMatchThreshold (defaults to 0 if not provided)
                            const searchTypeShape = inputs.get('searchType')?.ifObject()
                            const thresholdShape = searchTypeShape?.get('documentMatchThreshold')
                            if (thresholdShape) {
                                const thresholdValue = thresholdShape.ifNumber()?.getValue()
                                if (thresholdValue !== undefined && (thresholdValue < 0 || thresholdValue > 1)) {
                                    diagnostics.error(
                                        thresholdShape,
                                        `documentMatchThreshold must be between 0 and 1 (inclusive). Got: ${thresholdValue}`
                                    )
                                    return { success: false }
                                }
                            }
                        }
                    }
                }

                const toolRecords = tools
                    ? await Promise.all(
                          tools.map(async (tool) => {
                              // Working only with flattened structure - the tool object contains all properties
                              const toolObj = tool.asObject()

                              // Check if this is an OOB tool type (like 'web_automation')
                              // Map user-facing type to internal type first (e.g. 'search_retrieval' → 'rag')
                              // so the OOB lookup finds the correct sys_id
                              const toolType = toolObj.get('type')?.ifString()?.getValue()
                              const internalToolType = toolType === 'search_retrieval' ? 'rag' : toolType
                              const oobToolSysId = internalToolType
                                  ? OOB_TOOL_TYPE_TO_SYS_ID_MAP[internalToolType]
                                  : undefined

                              // If this is an OOB tool, we link to the existing tool rather than creating a new one
                              if (oobToolSysId) {
                                  // Link to existing tool - only create M2M record, no new tool record
                                  // createAgentToolM2MRecord will only use m2mSysOverrides property
                                  const agentToolM2MRecord = await createAgentToolM2MRecord(
                                      factory,
                                      callExpression,
                                      agentRecord.getId(),
                                      toolObj,
                                      oobToolSysId,
                                      SN_AI_AGENT_TOOL_M2M
                                  )

                                  // Return only the M2M record (no tool record created)
                                  return agentToolM2MRecord
                              }

                              // Create the tool record from details
                              // createAgentToolRecord will only use sysOverrides property
                              const agentToolRecord = await createAgentToolRecord(
                                  factory,
                                  callExpression,
                                  toolObj,
                                  SN_AI_AGENT_TOOL
                              )

                              // Create the M2M record using tool record
                              // createAgentToolM2MRecord will only use m2mSysOverrides property
                              const agentToolM2MRecord = await createAgentToolM2MRecord(
                                  factory,
                                  callExpression,
                                  agentRecord.getId(),
                                  toolObj,
                                  agentToolRecord.getId().getValue(),
                                  SN_AI_AGENT_TOOL_M2M
                              )

                              return agentToolRecord.with(agentToolM2MRecord)
                          })
                      )
                    : []

                // Create category mapping records
                const memoryCategoriesArray = arg.get('memoryCategories')?.ifArray()?.getElements() ?? []
                const validCategoryNames = memoryCategoriesArray
                    .map((categoryShape) => categoryShape.ifString()?.getValue())
                    .filter((name): name is string => !!name && name in MEMORY_CATEGORY_TO_SYS_ID_MAP)
                const categoryMappingRecords = await Promise.all(
                    validCategoryNames.map((categoryName) =>
                        createCategoryMappingRecord(
                            factory,
                            callExpression,
                            agentRecord.getId(),
                            MEMORY_CATEGORY_TO_SYS_ID_MAP[categoryName] ?? ''
                        )
                    )
                )

                // Collect all child records
                const childRecords = [
                    agentConfigRecord,
                    ...agentVersionRecords,
                    ...toolRecords,
                    ...triggerMappingRecords,
                    ...categoryMappingRecords,
                ]
                if (dataAccessRecords) {
                    childRecords.push(...dataAccessRecords)
                }
                // Add auto-generated ACL + role records when securityAcl was used
                if (securityAclRecords) {
                    childRecords.push(...securityAclRecords.allRecords)
                }
                // Add trigger config records separately (not as children)
                childRecords.push(...triggerConfigRecords.map((tc) => tc.record))

                return {
                    success: true,
                    value: agentRecord.with(...childRecords),
                }
            },
        },
    ],
})
